import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface Notice {
    id: string;
    title: string;
    notice_type: string;
    priority: string;
    content: string;
    summary: string;
    target_audience: string;
    publish_date: string;
    expiry_date: string;
    is_published: boolean;
    is_sticky: boolean;
    view_count: number;
    acknowledgment_required: boolean;
    created_by_name: string;
    created_at: string;
}

interface Props {
    onBack: () => void;
}

const NoticesCenter: React.FC<Props> = ({ onBack }) => {
    const baseUrl = useBaseUrl();
    const [notices, setNotices] = useState<Notice[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [selectedPriority, setSelectedPriority] = useState<string>('all');
    const [selectedAudience, setSelectedAudience] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(12);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);

    const noticeTypes = [
        { value: 'announcement', label: 'General Announcement', color: '#007bff', icon: 'bx-megaphone' },
        { value: 'alert', label: 'System Alert', color: '#dc3545', icon: 'bx-error' },
        { value: 'maintenance', label: 'Maintenance Notice', color: '#ffc107', icon: 'bx-wrench' },
        { value: 'policy', label: 'Policy Update', color: '#6610f2', icon: 'bx-shield' },
        { value: 'training', label: 'Training Announcement', color: '#28a745', icon: 'bx-book' },
        { value: 'emergency', label: 'Emergency Notice', color: '#fd7e14', icon: 'bx-error-circle' },
        { value: 'reminder', label: 'Reminder', color: '#17a2b8', icon: 'bx-bell' }
    ];

    const priorityLevels = [
        { value: 'low', label: 'Low Priority', color: '#6c757d' },
        { value: 'normal', label: 'Normal Priority', color: '#007bff' },
        { value: 'high', label: 'High Priority', color: '#fd7e14' },
        { value: 'urgent', label: 'Urgent', color: '#dc3545' },
        { value: 'critical', label: 'Critical', color: '#6f42c1' }
    ];

    const audiences = [
        { value: 'all', label: 'All Users' },
        { value: 'staff', label: 'All Staff' },
        { value: 'patients', label: 'All Patients' },
        { value: 'admins', label: 'Administrators' }
    ];

    useEffect(() => {
        loadNotices();
    }, [selectedType, selectedPriority, selectedAudience]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || loading || !hasMore) {
                return;
            }
            loadMoreNotices();
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, hasMore]);

    const loadNotices = async () => {
        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/notices/`;
            const params = new URLSearchParams();
            
            if (selectedType !== 'all') params.append('notice_type', selectedType);
            if (selectedPriority !== 'all') params.append('priority', selectedPriority);
            if (selectedAudience !== 'all') params.append('target_audience', selectedAudience);
            params.append('page_size', itemsPerPage.toString());

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                if (Array.isArray(response.data.results)) {
                    setNotices(response.data.results);
                    setTotalCount(response.data.count || 0);
                    setHasMore(!!response.data.next);
                } else if (Array.isArray(response.data)) {
                    setNotices(response.data);
                    setTotalCount(response.data.length);
                    setHasMore(false);
                }
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error loading notices:', error);
            setNotices([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreNotices = async () => {
        if (loading || !hasMore) return;

        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/notices/`;
            const params = new URLSearchParams();
            
            if (selectedType !== 'all') params.append('notice_type', selectedType);
            if (selectedPriority !== 'all') params.append('priority', selectedPriority);
            if (selectedAudience !== 'all') params.append('target_audience', selectedAudience);
            params.append('page_size', itemsPerPage.toString());
            params.append('page', (currentPage + 1).toString());

            url += `?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && Array.isArray(response.data.results)) {
                setNotices(prev => [...prev, ...response.data.results]);
                setHasMore(!!response.data.next);
                setCurrentPage(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error loading more notices:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTypeInfo = (type: string) => {
        return noticeTypes.find(t => t.value === type) || { label: type, color: '#6c757d', icon: 'bx-info-circle' };
    };

    const getPriorityInfo = (priority: string) => {
        return priorityLevels.find(p => p.value === priority) || { label: priority, color: '#6c757d' };
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isExpired = (expiryDate: string) => {
        return new Date(expiryDate) < new Date();
    };

    const viewNotice = (notice: Notice) => {
        const typeInfo = getTypeInfo(notice.notice_type);
        const priorityInfo = getPriorityInfo(notice.priority);
        
        Swal.fire({
            title: notice.title,
            html: `
                <div class="text-start">
                    <div class="d-flex justify-content-between mb-3">
                        <span class="badge" style="background-color: ${typeInfo.color}">
                            <i class="${typeInfo.icon} me-1"></i>
                            ${typeInfo.label}
                        </span>
                        <span class="badge" style="background-color: ${priorityInfo.color}">
                            ${priorityInfo.label}
                        </span>
                    </div>
                    <p><strong>Target Audience:</strong> ${notice.target_audience}</p>
                    <p><strong>Published:</strong> ${formatDate(notice.publish_date)}</p>
                    <p><strong>Expires:</strong> ${formatDate(notice.expiry_date)}</p>
                    <p><strong>Author:</strong> ${notice.created_by_name}</p>
                    <hr>
                    <div>${notice.content}</div>
                </div>
            `,
            width: '600px',
            showCloseButton: true,
            confirmButtonText: 'Close',
            customClass: {
                htmlContainer: 'text-start'
            }
        });
    };

    const filteredNotices = notices.filter(notice =>
        notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notice.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notice.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="container-fluid p-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">
                                <i className="bx bx-bell me-2 text-primary"></i>
                                Notices & Announcements
                            </h4>
                            <p className="text-muted mb-0">Stay updated with important announcements and notices</p>
                        </div>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={onBack}
                        >
                            <i className="bx bx-arrow-back me-2"></i>
                            Back to Resources
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-lg-3 col-md-6 mb-3">
                    <div className="position-relative">
                        <i className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <input
                            type="text"
                            className="form-control ps-5"
                            placeholder="Search notices..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                    >
                        <option value="all">All Types</option>
                        {noticeTypes.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedPriority}
                        onChange={(e) => setSelectedPriority(e.target.value)}
                    >
                        <option value="all">All Priorities</option>
                        {priorityLevels.map(priority => (
                            <option key={priority.value} value={priority.value}>
                                {priority.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedAudience}
                        onChange={(e) => setSelectedAudience(e.target.value)}
                    >
                        <option value="all">All Audiences</option>
                        {audiences.map(audience => (
                            <option key={audience.value} value={audience.value}>
                                {audience.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Results Summary */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">
                            Showing {filteredNotices.length} of {totalCount} notices
                        </span>
                    </div>
                </div>
            </div>

            {/* Notices Grid */}
            <div className="row">
                {filteredNotices.map(notice => {
                    const typeInfo = getTypeInfo(notice.notice_type);
                    const priorityInfo = getPriorityInfo(notice.priority);
                    const expired = isExpired(notice.expiry_date);

                    return (
                        <div key={notice.id} className="col-lg-4 col-md-6 mb-4">
                            <div className={`card h-100 ${notice.is_sticky ? 'border-warning' : ''} ${expired ? 'opacity-75' : ''}`}>
                                {notice.is_sticky && (
                                    <div className="card-header bg-warning py-1">
                                        <small className="text-dark">
                                            <i className="bx bx-pin me-1"></i>
                                            Pinned Notice
                                        </small>
                                    </div>
                                )}
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span 
                                            className="badge" 
                                            style={{ backgroundColor: typeInfo.color }}
                                        >
                                            <i className={`${typeInfo.icon} me-1`}></i>
                                            {typeInfo.label}
                                        </span>
                                        <span 
                                            className="badge" 
                                            style={{ backgroundColor: priorityInfo.color }}
                                        >
                                            {priorityInfo.label}
                                        </span>
                                    </div>
                                    
                                    <h6 className="card-title mb-2">{notice.title}</h6>
                                    <p className="card-text text-muted small mb-3">
                                        {notice.summary}
                                    </p>
                                    
                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            <i className="bx bx-user me-1"></i>
                                            For: {notice.target_audience}
                                        </small>
                                        <small className="text-muted d-block">
                                            <i className="bx bx-calendar me-1"></i>
                                            Published: {formatDate(notice.publish_date)}
                                        </small>
                                        <small className={`d-block ${expired ? 'text-danger' : 'text-muted'}`}>
                                            <i className="bx bx-time me-1"></i>
                                            Expires: {formatDate(notice.expiry_date)}
                                            {expired && <span className="ms-1">(Expired)</span>}
                                        </small>
                                    </div>
                                    
                                    <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            <i className="bx bx-show me-1"></i>
                                            {notice.view_count} views
                                        </small>
                                        <button 
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => viewNotice(notice)}
                                        >
                                            <i className="bx bx-show me-1"></i>
                                            View
                                        </button>
                                    </div>
                                    
                                    {notice.acknowledgment_required && (
                                        <div className="mt-2">
                                            <small className="text-warning">
                                                <i className="bx bx-check-circle me-1"></i>
                                                Acknowledgment Required
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Loading State */}
            {loading && (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">Loading notices...</div>
                </div>
            )}

            {/* No Results */}
            {!loading && filteredNotices.length === 0 && (
                <div className="text-center py-5">
                    <i className="bx bx-bell display-4 text-muted"></i>
                    <h5 className="mt-3 text-muted">No notices found</h5>
                    <p className="text-muted">Try adjusting your filters or search terms.</p>
                </div>
            )}

            {/* Infinite Scroll Indicator */}
            {!loading && hasMore && filteredNotices.length > 0 && (
                <div className="text-center py-3">
                    <small className="text-muted">Scroll down to load more notices</small>
                </div>
            )}

            <style>{`
                .card {
                    transition: transform 0.2s, box-shadow 0.2s;
                    border: 1px solid #e3e6f0;
                }
                
                .card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.15);
                }
                
                .badge {
                    font-size: 0.75rem;
                }
                
                .card-title {
                    font-size: 1rem;
                    font-weight: 600;
                    line-height: 1.3;
                }
                
                .card-text {
                    font-size: 0.875rem;
                    line-height: 1.4;
                }
            `}</style>
        </div>
    );
};

export default NoticesCenter;
